import type { ReactNode } from "react";

import { InputLabel } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";

import type { DatepickerType, PopoverDirectionType } from "../DatepickerV2";

import ReactTailwindDatepicker from "../DatepickerV2";
import { FieldTrigger } from "../DatepickerV2/components/FieldTrigger";
import { getShortcutButtonLabel } from "../DatepickerV2/helpers/shortcutLabel";
import { FieldError } from "../FieldError";
import { CalenderIcon, CrossIcon } from "../Icons";
import { ResponsiveInfoTooltip } from "../ResponsiveInfoTooltip";
import { Typo } from "../Typo";

const defaultContainerClasses = clsx(
  "relative h-10 w-full rounded-lg bg-[rgb(var(--ps-color-grey-100))] outline-none",
  "ease-ease transition-colors duration-300",
  "outline outline-1 outline-border-primary-medium hover:outline-border-blue-base",
  "focus-within:!outline-1 focus-within:!outline-[rgb(var(--ps-color-border-blue-base))]",
  "[&_button]:end-0 [&_button]:rounded-none [&_button]:rtl:right-auto [&_svg]:rtl:rotate-180",
  "[&_#ps-dp-days_button:disabled]:no-strike [&_#ps-dp-days_button:disabled]:text-[#03142E33]",
);

const defaultInputClasses = clsx(
  "h-full w-full text-ellipsis border-none bg-transparent py-2 pr-8 ps-4 text-base/5 text-[rgb(26,27,27)] antialiased outline-none placeholder:text-text-subdued placeholder:opacity-100",
);

// The "field" trigger owns its own shape/background/focus styling entirely —
// the container just needs to be a plain positioning wrapper, none of the
// "input" variant's outline/background/`[&_button]` overrides.
const fieldContainerClasses = "relative w-full";

export type DatepickerProps = Pick<
  DatepickerType,
  | "value"
  | "onChange"
  | "limitDays"
  | "i18n"
  | "configs"
  | "placeholder"
  | "inputId"
  | "minDate"
  | "disabled"
> & {
  label?: string | ReactNode;
  subLabel?: string;
  error?: string;
  allowClear?: boolean;
  tooltip?: string;
  onTooltipHover?: (isMobile: boolean) => void;
  isMobile?: boolean;
  containerClasses?: string;
  inputClasses?: string;
  displayVariant?: "input" | "field";
  /** Defaults to "down". Pass "up" when the trigger sits near the bottom of
   * a fixed-height container (e.g. a modal) so the calendar doesn't get
   * clipped below the fold. */
  popoverDirection?: PopoverDirectionType;
};

export const Datepicker = ({
  value,
  onChange,
  i18n,
  configs,
  placeholder,
  label,
  subLabel,
  error,
  inputId,
  allowClear = true,
  minDate,
  limitDays,
  tooltip,
  disabled = false,
  isMobile = false,
  onTooltipHover,
  containerClasses: containerClassesCustom,
  inputClasses: inputClassesCustom,
  displayVariant = "input",
  popoverDirection = "down",
}: DatepickerProps) => {
  const maxDate = new Date();
  const shortcutButtonLabel = getShortcutButtonLabel(value, configs);
  const containerClasses =
    containerClassesCustom ||
    (displayVariant === "field"
      ? fieldContainerClasses
      : defaultContainerClasses);
  const inputClasses = inputClassesCustom || defaultInputClasses;
  const hasLabel = Boolean(label || subLabel || tooltip);

  const LabelComponent = () => {
    if (typeof label === "string") {
      return (
        <>
          {label && inputId && <InputLabel value={label} htmlFor={inputId} />}
          {tooltip && (
            <ResponsiveInfoTooltip
              title={label}
              tooltipText={tooltip}
              isMobile={isMobile}
              onHover={onTooltipHover}
            />
          )}
        </>
      );
    }
    return <>{label}</>;
  };

  return (
    <div className="rtl flex w-full flex-col gap-y-2">
      {hasLabel && (
        <label htmlFor={inputId} className="flex flex-col gap-1">
          <div className="flex items-center gap-1">
            <LabelComponent />
          </div>
          {subLabel && (
            <Typo element="p" type="bodyXS" textColor="text-text-secondary">
              {subLabel}
            </Typo>
          )}
        </label>
      )}
      <div className="relative">
        <ReactTailwindDatepicker
          inputId={inputId}
          value={value}
          separator="-"
          classNames={{
            footer: () =>
              "flex items-center justify-end pb-2.5 pt-3 border-t border-gray-300 dark:border-gray-700 [&_div]:rtl:gap-2 [&_button]:rounded-md",
          }}
          containerClassName={containerClasses}
          inputClassName={inputClasses}
          toggleClassName={allowClear ? "" : "hidden"}
          toggleIcon={(isEmpty) =>
            isEmpty ? (
              <CalenderIcon
                className="text-text-subdued"
                width={20}
                height={20}
              />
            ) : (
              <CrossIcon className="text-text-subdued" width={20} height={20} />
            )
          }
          trigger={
            displayVariant === "field"
              ? (triggerProps) => (
                  <FieldTrigger
                    {...triggerProps}
                    allowClear={allowClear}
                    placeholder={placeholder}
                    shortcutButtonLabel={shortcutButtonLabel}
                  />
                )
              : undefined
          }
          readOnly
          disabled={disabled}
          onChange={onChange}
          limitDays={limitDays}
          primaryColor="blue"
          configs={configs}
          placeholder={placeholder}
          i18n={i18n}
          showShortcuts={true}
          showFooter={true}
          displayFormat="DD MMM YYYY"
          popoverDirection={popoverDirection}
          minDate={minDate}
          maxDate={maxDate}
        />
        {!allowClear && displayVariant !== "field" && (
          <CalenderIcon
            className="pointer-events-none absolute right-3 top-2.5 text-text-subdued"
            width={20}
            height={20}
          />
        )}
      </div>
      {error && <FieldError text={error} className="text-text-error" />}
    </div>
  );
};
